import { Inject } from '@odinjs/odin';

import { catchRenderException } from '@philips-emr/hpms-components';
import { ButtonAPI, DynamicFieldsAPI, FormAPI, Metadata, PanelAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnChange, OnClick } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { format } from '@philips-emr/tws-core/lib/services/model-service';

import { FragmentNames, InformacoesAdicionaisAliases } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { ReportService } from '../../services';
import BaseController from '../base/base-controller';

interface Relatorio {
  cdRelatorio: string;
  cdClassifRelat: string;
}

@Controller({ fragment: FragmentNames.INFORMACOES_ADICIONAIS })
export default class InformacoesAdicionaisController extends BaseController {

  @Inject(InformacoesAdicionaisAliases.PANEL_DYNAMIC)
  private panelDynamic: PanelAPI;
  @Inject(InformacoesAdicionaisAliases.FORM_RELATORIO)
  private formRelatorio: FormAPI;
  @Inject(InformacoesAdicionaisAliases.FORM_DYNAMIC)
  private formDynamic: FormAPI;
  @Inject(InformacoesAdicionaisAliases.DYNAMIC_FIELDS)
  private dynamicFields: DynamicFieldsAPI;
  @Inject(InformacoesAdicionaisAliases.BUTTON_GERAR)
  private buttonGerar: ButtonAPI;

  @OnChange(InformacoesAdicionaisAliases.FIELD_RELATORIO)
  onChangeRelatorio({ value }: ChangeEvent<{ nrSequencia: string }>): void {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    if (!value) {
      this.formDynamic.setRecord({});
      this.panelDynamic.setVisible(false);
      this.buttonGerar.setEnabled(false);
      return;
    }

    const { nrSequencia } = value;
    this.buttonGerar.setEnabled(!!nrSequencia);

    HttpService.get<Metadata>(Endpoints.GET.PARAMETROS_DINAMICOS, { nrSeqBeneficiario, nrSeqRelatorio: nrSequencia })
      .then(({ body }) => {
        if (body) {
          this.panelDynamic.setVisible(true);
          this.formDynamic.setRecord({});
          this.dynamicFields.setMetadata(body);
        } else {
          this.panelDynamic.setVisible(false);
        }
      })
      .catch(() => this.panelDynamic.setVisible(false));
  }

  @OnClick(InformacoesAdicionaisAliases.BUTTON_GERAR)
  onClickGerar(): void {
    if (this.panelDynamic.isVisible()) {
      this.formDynamic.submit();

      if (!this.formDynamic.isValid()) {
        return;
      }
    }

    const { nrSeqBeneficiario } = this.context.getBulkData();
    const params = this.getParams();
    const { relatorio } = this.formRelatorio.getRecord() as { relatorio: Relatorio };

    this.setSubmitting(true);

    ReportService.openReport({
      cdGrupo: '13',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
      parametrosRelatorio: params,
      record: [{
        cdRelatorio: relatorio.cdRelatorio,
        cdClassifRelat: relatorio.cdClassifRelat,
      }],
    })
      .finally(() => this.setSubmitting(false));
  }

  private setSubmitting(value: boolean): void {
    this.formRelatorio.setState({ submitting: value });
    catchRenderException(() => this.formDynamic.setState({ submitting: value }));
    this.buttonGerar.setState({ loading: value });
  }

  private getParams(): {} {
    let params = {};

    if (this.panelDynamic.isVisible()) {
      const record = this.formDynamic.getRecord();
      Object.keys(record).forEach(k => {
        if (record[k]) {
          if (typeof record[k] === 'object') {
            const value = record[k] as Record<string, Value>;
            params = { ...params, [k]: value.value };
          } else if (typeof record[k] === 'string') {
            const value = record[k] as Record<string, Value>;
            params = { ...params, [k]: value };
          } else {
            const value = format('date', record[k]);
            params = { ...params, [k]: value };
          }
        }
      });
    }

    return params;
  }
}
